/***
 *
 *   SCREENING PDF BUTTON
 *   Downloads the report for one check of a manual screening row, from the
 *   PEP/Sanctions or Adverse Media results page. KYC rows render nothing: they
 *   keep the KYC-tab Download PDF.
 *
 *   PROPS
 *   row: the verification row the page shows
 *   check: "sanctions" or "adverse_media", the page's check
 *
 **********/

import React, { useContext, useRef, useState } from "react";
import { Button, ViewContext } from "components/lib";
import Axios from "axios";
import { readBlobErrorMessage } from "../entity-onboarding/pdf-error";
import { canDownloadScreeningPdf, screeningPdfFilename } from "./screening-row";

// A combined screen has one report per check, so the label names the check.
const LABELS = {
  sanctions: "Download PEP/Sanctions PDF",
  adverse_media: "Download Adverse Media PDF",
};
const PDF_REQUEST_TIMEOUT_MS = 30000;

export function ScreeningPdfButton({ row, check }) {
  const viewContext = useContext(ViewContext);
  const [downloading, setDownloading] = useState(false);
  // Button does not forward `disabled`, and `loading` only paints a spinner,
  // so this ref is what stops a second request while one runs.
  const inFlight = useRef(false);

  if (!canDownloadScreeningPdf(row, check)) return null;

  async function download() {
    if (inFlight.current) return;

    let objectUrl;
    inFlight.current = true;
    setDownloading(true);
    try {
      const res = await Axios.get(
        `/api/verification/screening-pdf/${encodeURIComponent(row.id)}?check=${check}`,
        { responseType: "blob", timeout: PDF_REQUEST_TIMEOUT_MS }
      );
      objectUrl = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      // The blob download ignores Content-Disposition, so name the file here.
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = screeningPdfFilename(row, check);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (err) {
      viewContext.notification.show(
        await readBlobErrorMessage(err, "Could not download the screening report"),
        "error",
        true
      );
    } finally {
      // An anchor download commits synchronously on click, so revoking here
      // is safe on success, and on error it frees any URL already created.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      inFlight.current = false;
      setDownloading(false);
    }
  }

  return (
    <Button
      small
      outline
      text={downloading ? "Downloading…" : LABELS[check]}
      loading={downloading}
      action={download}
    />
  );
}
